/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {Injectable, TrackByFn} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {Subscription} from 'rxjs/Subscription';

import {FiltersProvider} from './filters';
import {Items} from './items';

let /** @type {?} */ nbSelection: number = 0;
export type SelectionType = number;
export let SelectionType: any = {};
SelectionType.None = 0;
SelectionType.Single = 1;
SelectionType.Multi = 2;
SelectionType[SelectionType.None] = "None";
SelectionType[SelectionType.Single] = "Single";
SelectionType[SelectionType.Multi] = "Multi";

export class Selection {
public id: string;
/**
 * @param {?} _items
 * @param {?} _filters
 */
constructor(private _items: Items,
private _filters: FiltersProvider) {
        this.id = "clr-dg-selection" + (nbSelection++);

        this._filtersSub = this._filters.change.subscribe(() => {
            if (!this._selectable) {
                return;
            }
            this.clearSelection();
        });

        this._itemsSub = this._items.allChanges.subscribe((updatedItems) => {
            if (!this._selectable) {
                return;
            }
            let leftOver: any[];
            if (this._items.trackBy) {
                const trackBy: TrackByFn = this._items.trackBy;
                const updatedTracked: any[] = updatedItems.map((item, index) => trackBy(index, item));
                leftOver = this.current.filter((selected, index) => {
                    return updatedTracked.indexOf(trackBy(index, selected)) > -1;
                });
            } else {
                leftOver = this.current.filter(selected => updatedItems.indexOf(selected) > -1);
            }
            if (this.current.length !== leftOver.length) {
                // TODO: Discussed this with Eudes and this is fine for now.
                // But we need to figure out a different pattern for the
                // child triggering the parent change detection problem.
                // Using setTimeout for now to fix this.
                setTimeout(() => {
                    this.current = leftOver;
                }, 0);
            }
        });
    }
/**
 * @return {?}
 */
public clearSelection(): void {
        this.current.length = 0;
        this.emitChange();
    }
private _selectionType: SelectionType = SelectionType.None;
/**
 * @return {?}
 */
public get selectionType(): SelectionType {
        return this._selectionType;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set selectionType(value: SelectionType) {
        if (value === this.selectionType) {
            return;
        }
        this._selectionType = value;
        if (value === SelectionType.None) {
            delete this.current;
        } else {
            this.current = [];
        }
    }
public rowSelectionMode: boolean = false;
/**
 * @return {?}
 */
private get _selectable(): boolean {
        return (this._selectionType === SelectionType.Multi) || (this._selectionType === SelectionType.Single);
    }
/**
 * Ignore items changes in the same change detection cycle.
 */
private debounce: boolean = false;
/**
 * Subscriptions to the other providers changes.
 */
private _itemsSub: Subscription;
private _filtersSub: Subscription;
/**
 * Cleans up our subscriptions to other providers
 * @return {?}
 */
public destroy() {
        this._itemsSub.unsubscribe();
        this._filtersSub.unsubscribe();
    }
/**
 * The current selection in single selection type
 */
private _currentSingle: any;
/**
 * @return {?}
 */
public get currentSingle(): any {
        return this._currentSingle;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set currentSingle(value: any) {
        if (value === this._currentSingle) {
            return;
        }
        this._currentSingle = value;
        this.emitChange();
        // Ignore items changes in the same change detection cycle.
        this.debounce = true;
        setTimeout(() => this.debounce = false);
    }
/**
 * The current selection
 */
private _current: any[];
/**
 * @return {?}
 */
public get current(): any[] {
        return this._current;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set current(value: any[]) {
        this._current = value;
        this.emitChange();
        // Ignore items changes in the same change detection cycle.
        this.debounce = true;
        setTimeout(() => this.debounce = false);
    }
/**
 * The Observable that lets other classes subscribe to selection changes
 */
private _change = new Subject<any[]|any>();
/**
 * @return {?}
 */
private emitChange() {
        if (this._selectionType === SelectionType.Single) {
            this._change.next(this.currentSingle);
        } else if (this._selectionType === SelectionType.Multi) {
            this._change.next(this.current);
        }
    }
/**
 * @return {?}
 */
public get change(): Observable<any[]|any> {
        return this._change.asObservable();
    }
/**
 * Checks if an item is currently selected
 * @param {?} item
 * @return {?}
 */
public isSelected(item: any): boolean {
        if (this._selectionType === SelectionType.Single) {
            return this.currentSingle === item;
        } else if (this._selectionType === SelectionType.Multi) {
            return this.current.indexOf(item) >= 0;
        }
        return false;
    }
/**
 * Selects or deselects an item
 * @param {?} item
 * @param {?} selected
 * @return {?}
 */
public setSelected(item: any, selected: boolean) {
        switch (this._selectionType) {
            case SelectionType.None:
                break;
            case SelectionType.Single:
                // in single selection, set currentSingle method should be used
                break;
            case SelectionType.Multi:
                const /** @type {?} */ index = this.current.indexOf(item);
                if (index >= 0 && !selected) {
                    this.current.splice(index, 1);
                    this.emitChange();
                } else if (index < 0 && selected) {
                    this.current.push(item);
                    this.emitChange();
                }
                break;
            default:
                break;
        }
    }
/**
 * Checks if all currently displayed items are selected
 * @return {?}
 */
public isAllSelected(): boolean {
        if ((this._selectionType !== SelectionType.Multi) || !this._items.displayed) {
            return false;
        }
        const /** @type {?} */ displayedItems: any[] = this._items.displayed;
        const /** @type {?} */ nbDisplayed = this._items.displayed.length;
        if (nbDisplayed < 1) {
            return false;
        }
        const /** @type {?} */ temp: any[] = displayedItems.filter(item => this.current.indexOf(item) > -1);
        return temp.length === displayedItems.length;
    }
/**
 * Selects or deselects all currently displayed items
 * @return {?}
 */
public toggleAll() {
        if (this._selectionType === SelectionType.None || this._selectionType === SelectionType.Single) {
            return;
        }
        /*
         * If every currently displayed item is already selected, we clear them.
         * If at least one item isn't selected, we select every currently displayed item.
         */
        if (this.isAllSelected()) {
            this.current = this.current.filter(item => {
                return this._items.displayed.indexOf(item) < 0;
            });
        } else {
            this._items.displayed.forEach(item => {
                if (this.current.indexOf(item) < 0) {
                    this.current.push(item);
                }
            });
            this.emitChange();
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Items, },
{type: FiltersProvider, },
];
}

function Selection_tsickle_Closure_declarations() {
/** @type {?} */
Selection.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Selection.ctorParameters;
/** @type {?} */
Selection.prototype.id;
/** @type {?} */
Selection.prototype._selectionType;
/** @type {?} */
Selection.prototype.rowSelectionMode;
/**
 * Ignore items changes in the same change detection cycle.
 * @type {?}
 */
Selection.prototype.debounce;
/**
 * Subscriptions to the other providers changes.
 * @type {?}
 */
Selection.prototype._itemsSub;
/** @type {?} */
Selection.prototype._filtersSub;
/**
 * The current selection in single selection type
 * @type {?}
 */
Selection.prototype._currentSingle;
/**
 * The current selection
 * @type {?}
 */
Selection.prototype._current;
/**
 * The Observable that lets other classes subscribe to selection changes
 * @type {?}
 */
Selection.prototype._change;
/** @type {?} */
Selection.prototype._items;
/** @type {?} */
Selection.prototype._filters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
